import { useCallback } from "react";

import { useTypedDispatch, useTypedSelector } from "../../../../../redux/store";
import {
	State,
	accountingAction as accounting,
} from "../../../../../redux/reducers/Accounting";

export type UseAccountingReplenishments = {
	data: State["replenishments"]["data"];
	filters: State["replenishments"]["filters"];
	setItems: (data: State["replenishments"]["data"]["items"]) => void;
	setCounters: (data: State["replenishments"]["data"]["counters"]) => void;
	setDateFrom: (date: Date) => void;
	setDateTo: (date: Date) => void;
	setQuery: (query: State["replenishments"]["filters"]["query"]) => void;
	modifyFiltersByMultyFilters: (
		data: State["replenishments"]["filters"]["byMultyFilters"],
	) => void;
	reset: () => void;
};

/** Redux state of the accounting "replenish performers balance" tab. */
export const useAccountingReplenishments = (): UseAccountingReplenishments => {
	const dispatch = useTypedDispatch();
	const data = useTypedSelector(
		(state) => state.accounting.replenishments.data,
	);
	const filters = useTypedSelector(
		(state) => state.accounting.replenishments.filters,
	);

	const setItems = useCallback<UseAccountingReplenishments["setItems"]>(
		(items) => {
			dispatch(accounting.replenishments.data.setItems(items));
		},
		[dispatch],
	);
	const setCounters = useCallback<UseAccountingReplenishments["setCounters"]>(
		(counters) => {
			dispatch(accounting.replenishments.data.setCounters(counters));
		},
		[dispatch],
	);
	const setDateFrom = useCallback(
		(date: Date) => {
			dispatch(accounting.replenishments.filters.setDateFrom(date));
		},
		[dispatch],
	);
	const setDateTo = useCallback(
		(date: Date) => {
			dispatch(accounting.replenishments.filters.setDateTo(date));
		},
		[dispatch],
	);
	const setQuery = useCallback<UseAccountingReplenishments["setQuery"]>(
		(query) => {
			dispatch(accounting.replenishments.filters.setQuery(query));
		},
		[dispatch],
	);
	const modifyFiltersByMultyFilters = useCallback<
		UseAccountingReplenishments["modifyFiltersByMultyFilters"]
	>(
		(data) => {
			dispatch(
				accounting.replenishments.filters.setFiltersByMultyFilters(
					data,
				),
			);
		},
		[dispatch],
	);
	const reset = useCallback(() => {
		dispatch(accounting.replenishments.data.reset(undefined));
		dispatch(accounting.replenishments.filters.reset(undefined));
	}, [dispatch]);

	return {
		data,
		filters,
		setItems,
		setCounters,
		setDateFrom,
		setDateTo,
		setQuery,
		modifyFiltersByMultyFilters,
		reset,
	};
};

export default useAccountingReplenishments;
